import React, { useMemo, useState } from 'react';
import {
  PiCheckCircle, PiCaretDown, PiTruck, PiShieldCheck, PiArrowsClockwise, PiLightning, PiTag,
} from 'react-icons/pi';
import {
  PreviewFrame, Stars, PressBar, avatarBg, initialsFor, fillTokens,
} from './_shared';
import {
  addToCart, buyNow, pickVariantId,
} from '../../../utils/shopifyStorefront';
import { pingLandingPageCta, lpLineProperty } from '../../../utils/landingAttribution';

// Single Product Funnel — PDP-style landing page. Renders saved section
// content with sensible fallbacks, live-binds to the Shopify product via
// the storefront AJAX API (when handle is known), and wires the CTAs to
// Shopify's /cart/add and /checkout permalinks. Attribution + discount
// code are carried through from URL params on every button.
//
// All copy strings are editable from the admin template editor and fall
// back to the values below when unset. Only HONEST fallbacks live here —
// fabricated content (fake press logos, reviews, ratings, scarcity, save
// badges, placeholder prices) was removed so the live page only claims
// what the merchant actually configured, matching the admin preview.

const DEFAULTS = {
  announcement: '🚚 Free shipping on orders over $50 · 30-day money-back guarantee',
  stockText: 'In stock · Ships in 24 hours',
  trust: [
    { icon: 'truck',    text: 'Free shipping $50+' },
    { icon: 'returns',  text: '30-day returns' },
    { icon: 'shield',   text: '2-year warranty' },
    { icon: 'fast',     text: 'Ships in 24h' },
  ],
  // Aligned to the schema's defaultContent list so an emptied editor list and a legacy page agree.
  benefits: ['Premium quality', 'Thoughtfully designed', '30-day guarantee'],
  comparisonRows: [
    { feature: 'Premium materials', us: true, them: false },
    { feature: 'Lifetime warranty', us: true, them: false },
    { feature: 'Free shipping', us: true, them: false },
    { feature: '30-day returns', us: true, them: true },
    { feature: 'Independently tested', us: true, them: false },
  ],
};

const TRUST_ICONS = {
  truck:   PiTruck,
  returns: PiArrowsClockwise,
  shield:  PiShieldCheck,
  fast:    PiLightning,
  check:   PiCheckCircle,
};

// Number-or-cents → "12.99". Sections store priceLabel as either a numeric
// string ("12.99"), a formatted string ("$12.99"), or null; we normalize.
function moneyOrNull(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'string' && v.startsWith('$')) return v;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `$${n.toFixed(2)}`;
}

export default function SingleProductFunnelTemplate({
  sections, entity, viewport, theme, editable, onSectionClick,
  product, attribution, discountCode, page,
}) {
  const hero = sections.hero || {};
  const announcement = sections.announcement || {};
  const benefits = sections.benefits || {};
  const reviews = sections.reviews || {};
  const faq = sections.faq || {};
  const finalCta = sections.finalCta || {};
  const stickyCta = sections.stickyCta || {};
  const comparison = sections.comparison || {};
  const guarantee = sections.guarantee || {};

  const accent = theme?.primaryColor || '#8661FF';

  // Live product wins over the saved snapshot wins over the binding entity
  // (admin preview path). Each field falls back independently so a missing
  // live product doesn't erase a saved hero image.
  const merged = useMemo(() => {
    const live = product || {};
    const liveImage = live.featured_image
      || live.images?.[0]
      || null;
    const livePrice = Number.isFinite(Number(live.price)) ? Number(live.price) / 100 : null;
    const liveCompare = Number.isFinite(Number(live.compare_at_price)) ? Number(live.compare_at_price) / 100 : null;
    const ent = entity || {};
    const title = hero.headline || live.title || ent.title || 'Featured product';
    const heroImage = hero.heroImageUrl || liveImage || ent.imageUrl || null;
    // No invented placeholder price — when nothing resolves (no saved label,
    // no live product, no snapshot) the price row and CTA price suffix are
    // hidden instead of showing a made-up $49.00.
    const priceStr = moneyOrNull(hero.priceLabel)
      || (livePrice != null ? `$${livePrice.toFixed(2)}` : null)
      || (ent.price != null ? `$${Number(ent.price).toFixed(2)}` : null);
    const compareStr = moneyOrNull(hero.compareAtLabel)
      || (liveCompare != null && liveCompare > (livePrice || 0) ? `$${liveCompare.toFixed(2)}` : null);
    const savePctNum = (() => {
      const p = parseFloat(String(priceStr).replace(/[^0-9.]/g, ''));
      const c = compareStr ? parseFloat(String(compareStr).replace(/[^0-9.]/g, '')) : null;
      if (!c || !Number.isFinite(p) || c <= p) return null;
      return Math.round(((c - p) / c) * 100);
    })();
    const variantId = pickVariantId(live, hero.productVariantId);
    return { title, heroImage, priceStr, compareStr, savePctNum, variantId };
  }, [hero, product, entity]);

  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [cartError, setCartError] = useState(false);

  const handleAddToCart = async () => {
    if (!merged.variantId || editable) return;
    // Cart-intent click → stats.ctaClicks, regardless of add outcome.
    pingLandingPageCta(page);
    setAdding(true);
    setCartError(false);
    const props = lpLineProperty(page);
    const r = await addToCart({
      variantId: merged.variantId,
      quantity,
      properties: Object.keys(props).length ? props : undefined,
    });
    setAdding(false);
    if (r.ok) {
      window.location.href = '/cart';
    } else {
      // A silent un-busy reads as a dead button — tell the buyer it failed
      // (sold out between page load and click, cart API hiccup, …).
      setCartError(true);
    }
  };

  const handleBuyNow = () => {
    if (!merged.variantId || editable) return;
    pingLandingPageCta(page);
    buyNow({
      variantId: merged.variantId,
      quantity,
      // Permalinks can't carry line properties — the page id rides as a cart
      // attribute instead; the webhook checks both.
      extraAttributes: page?.id ? [{ name: '_brella_lp_id', value: page.id }] : [],
    });
  };

  // Show a save badge only from a merchant-set value or a discount computed
  // from a real compare-at price. Never invent one.
  const saveBadgeText = hero.saveBadge
    || (merged.savePctNum ? `SAVE ${merged.savePctNum}%` : '');
  // Price-row discount chip wording — merchant-editable, still gated on a real
  // compare-at discount. Explicit '' hides the chip.
  const saveChipText = merged.savePctNum != null
    ? fillTokens(hero.saveChipText ?? 'SAVE {pct}%', { pct: merged.savePctNum })
    : '';
  const announcementText = announcement.text || DEFAULTS.announcement;
  const announcementEnabled = announcement.enabled !== false;
  // Scarcity is opt-IN and merchant-authored only — no fabricated default copy.
  const scarcityText = hero.scarcityText || '';
  const scarcityEnabled = hero.showScarcity === true;
  const stockText = hero.stockText || DEFAULTS.stockText;
  const stockEnabled = hero.showStock !== false;
  // undefined → legacy defaults (old saved pages); explicit [] → merchant
  // emptied the list → hide the grid.
  const trustItems = Array.isArray(hero.trustItems) ? hero.trustItems : DEFAULTS.trust;
  const pressLogos = (Array.isArray(hero.pressLogos) ? hero.pressLogos : []).filter((l) => String(l ?? '').trim());
  const ctaLabel = hero.ctaLabel || 'Add to cart';
  const ctaBusyLabel = hero.ctaBusyLabel || 'Adding…';
  const buyNowLabel = hero.buyNowLabel || 'Buy now → 1-tap checkout';
  const showBuyNow = hero.showBuyNow !== false;
  const showCtaPrice = hero.showCtaPrice !== false;
  const ctaPriceSuffix = showCtaPrice && merged.priceStr ? ` · ${merged.priceStr}` : '';

  // Ratings/reviews render ONLY when the merchant supplied real values —
  // no fabricated stars (4.8), counts (247), or testimonials.
  const hasRating = reviews.starsAvg != null && reviews.starsAvg !== '';
  const starsAvgNum = hasRating ? Number(reviews.starsAvg) : null;
  const hasReviewCount = reviews.reviewCount != null && reviews.reviewCount !== '';
  const reviewItems = Array.isArray(reviews.items) ? reviews.items : [];
  const hasReviews = reviewItems.length > 0;
  const ratingSummaryText = hasRating && hasReviewCount
    ? fillTokens(reviews.ratingSummaryText ?? '{avg} from {count} verified reviews', { avg: starsAvgNum.toFixed(1), count: reviews.reviewCount })
    : '';
  const verifiedLabel = reviews.verifiedLabel ?? 'Verified buyer';
  // undefined → legacy fallback lists; explicit [] hides the grid/table.
  const benefitItems = Array.isArray(benefits.items) ? benefits.items : DEFAULTS.benefits;
  const comparisonRows = Array.isArray(comparison?.rows) ? comparison.rows : DEFAULTS.comparisonRows;
  const finalCtaEyebrow = finalCta.eyebrow ?? 'JOIN OUR HAPPY CUSTOMERS';

  // Pre-built CTA so editor and storefront share one button definition.
  const renderPrimaryCta = (label, opts = {}) => (
    <button
      type="button"
      onClick={handleAddToCart}
      disabled={adding || !merged.variantId}
      style={{
        background: opts.color || accent,
        color: opts.textColor || '#fff',
        border: 'none',
        padding: opts.big ? '16px 28px' : '14px 22px',
        borderRadius: 'var(--lp-radius, 8px)',
        fontSize: opts.big ? 16 : 15,
        fontWeight: 700,
        cursor: adding || !merged.variantId ? 'not-allowed' : 'pointer',
        whiteSpace: 'nowrap',
        width: opts.full ? '100%' : undefined,
        boxShadow: opts.big ? '0 4px 14px rgba(0,0,0,0.12)' : 'none',
        opacity: !merged.variantId ? 0.6 : 1,
      }}
    >
      {adding ? ctaBusyLabel : (label || ctaLabel)}{ctaPriceSuffix}
    </button>
  );

  return (
    <PreviewFrame viewport={viewport} accent={accent} theme={theme} sections={sections} editable={editable} onSectionClick={onSectionClick}>
      {/* Add-to-cart failure toast — covers every CTA instance (hero, final, sticky) */}
      {cartError && (
        <div style={{
          position: 'fixed', bottom: 84, left: '50%', transform: 'translateX(-50%)',
          background: '#B3261E', color: '#fff', borderRadius: 8,
          padding: '10px 18px', fontSize: 13, fontWeight: 600,
          boxShadow: '0 6px 20px rgba(0,0,0,0.25)', zIndex: 60, maxWidth: '90vw',
        }}>
          Couldn’t add to cart — please try again.
        </div>
      )}
      {/* ANNOUNCEMENT */}
      {announcementEnabled && (
        <div data-lp-section="announcement" style={{
          background: announcement.backgroundColor || '#1F1B33',
          color: announcement.textColor || '#fff', textAlign: 'center',
          padding: '8px 16px', fontSize: 12, fontWeight: 600,
        }}>
          {announcementText}
        </div>
      )}

      {/* DISCOUNT CALLOUT — only when arrived via attributed link */}
      {discountCode && (
        <div
          data-lp-section="announcement"
          style={{
            background: '#FFF6E0', color: '#8a6500',
            textAlign: 'center', padding: '6px 16px',
            fontSize: 12, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <PiTag size={13} />
          <span data-lp-field="discountCalloutText">
            {fillTokens(announcement.discountCalloutText || 'Discount {code} applied automatically at checkout', { code: discountCode })}
          </span>
        </div>
      )}

      {/* HERO */}
      <section data-lp-section="hero" style={{
        display: 'grid',
        gridTemplateColumns: viewport === 'mobile' ? '1fr' : '1.1fr 1fr',
        gap: viewport === 'mobile' ? 24 : 48,
        padding: viewport === 'mobile' ? 20 : '40px 48px',
        alignItems: 'flex-start',
        maxWidth: 1280, margin: '0 auto',
      }}>
        {/* Gallery */}
        <div>
          <div style={{
            aspectRatio: '1/1', background: '#F2EEFF', borderRadius: 12,
            backgroundImage: merged.heroImage ? `url(${merged.heroImage})` : undefined,
            backgroundSize: 'cover', backgroundPosition: 'center',
            marginBottom: 12, position: 'relative',
          }}>
            {saveBadgeText && (
              <div style={{ position: 'absolute', top: 16, left: 16, background: '#FF4757', color: '#fff', fontSize: 11, fontWeight: 800, padding: '6px 10px', borderRadius: 4, letterSpacing: 0.5 }}>
                {saveBadgeText}
              </div>
            )}
          </div>
          {viewport !== 'mobile' && Array.isArray(product?.images) && product.images.length > 1 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {product.images.slice(0, 4).map((src, i) => (
                <div key={i} style={{
                  aspectRatio: '1/1', background: '#F2EEFF', borderRadius: 6,
                  backgroundImage: `url(${src})`,
                  backgroundSize: 'cover', backgroundPosition: 'center',
                  border: i === 0 ? `2px solid ${accent}` : '2px solid transparent',
                  opacity: i === 0 ? 1 : 0.7,
                }} />
              ))}
            </div>
          )}
        </div>

        {/* Buy box */}
        <div>
          {hasRating && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Stars n={starsAvgNum} size={16} />
              <span style={{ fontSize: 13, color: '#4a4458', fontWeight: 600 }}>{starsAvgNum.toFixed(1)}</span>
              {hasReviewCount && (
                <span style={{ fontSize: 13, color: '#7d7a86', textDecoration: 'underline' }}>({reviews.reviewCount} reviews)</span>
              )}
            </div>
          )}
          <h1 style={{ fontSize: viewport === 'mobile' ? 28 : 36, fontWeight: 800, margin: '0 0 8px', lineHeight: 1.15, letterSpacing: -0.5 }}>
            {merged.title}
          </h1>
          <p style={{ fontSize: 15, color: '#4a4458', lineHeight: 1.6, margin: '0 0 20px' }}>
            {hero.subheadline || 'The product everyone is talking about — designed for daily use, built to last.'}
          </p>

          {/* Price */}
          {merged.priceStr && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
              <span style={{ fontSize: 32, fontWeight: 800, color: '#1F1B33' }}>{merged.priceStr}</span>
              {merged.compareStr && (
                <span style={{ fontSize: 18, color: '#9892a3', textDecoration: 'line-through' }}>{merged.compareStr}</span>
              )}
              {merged.compareStr && saveChipText && (
                <span data-lp-field="saveChipText" style={{ background: '#FFE5E8', color: '#FF4757', fontSize: 12, fontWeight: 700, padding: '4px 8px', borderRadius: 4 }}>
                  {saveChipText}
                </span>
              )}
            </div>
          )}
          {stockEnabled && (
            <div style={{ fontSize: 13, color: '#22B364', fontWeight: 600, marginBottom: 20, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22B364' }} />
              {stockText}
            </div>
          )}

          {/* Quantity + CTA */}
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 10, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #E5E0F2', borderRadius: 8, padding: '0 4px' }}>
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                style={{ width: 36, height: 44, border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: '#4a4458' }}
                aria-label="Decrease quantity"
              >−</button>
              <span style={{ width: 28, textAlign: 'center', fontSize: 15, fontWeight: 600 }}>{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.min(99, q + 1))}
                style={{ width: 36, height: 44, border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: '#4a4458' }}
                aria-label="Increase quantity"
              >+</button>
            </div>
            {renderPrimaryCta(ctaLabel, { big: true, full: true })}
          </div>
          {showBuyNow && (
            <button
              type="button"
              onClick={handleBuyNow}
              disabled={!merged.variantId}
              style={{
                width: '100%', padding: '14px', background: '#1F1B33', color: '#fff',
                border: 'none', borderRadius: 'var(--lp-radius, 8px)', fontSize: 15, fontWeight: 700,
                cursor: merged.variantId ? 'pointer' : 'not-allowed', marginBottom: 20,
                opacity: merged.variantId ? 1 : 0.6,
              }}
            >
              {buyNowLabel}
            </button>
          )}

          {/* Trust indicators */}
          {trustItems.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13, color: '#4a4458' }}>
              {trustItems.map((it, i) => {
                const Icon = TRUST_ICONS[it.icon] || PiCheckCircle;
                return (
                  <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Icon size={18} style={{ color: accent }} /> {it.text || it.label || it}
                  </span>
                );
              })}
            </div>
          )}

          {scarcityEnabled && scarcityText && (
            <div style={{
              marginTop: 16, padding: 12, background: '#FFF6E0', borderRadius: 8,
              fontSize: 13, color: '#8a6500', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {scarcityText}
            </div>
          )}
        </div>
      </section>

      {hero.showPressBar !== false && pressLogos.length > 0 && (
        <div data-lp-section="hero">
          <PressBar logos={pressLogos} label={hero.pressLabel} />
        </div>
      )}

      {/* BENEFITS */}
      <section data-lp-section="benefits" style={{ padding: viewport === 'mobile' ? '48px 24px' : '72px 48px', background: '#FAFAFC' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ fontSize: 12, color: accent, fontWeight: 700, letterSpacing: 1.5, marginBottom: 8 }}>{benefits.eyebrow || 'WHY IT WORKS'}</div>
            <h2 style={{ fontSize: 30, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>
              {benefits.headline || 'Why customers love it'}
            </h2>
          </div>
          {benefitItems.length > 0 && (
            <div style={{ display: 'grid', gap: 20,
              gridTemplateColumns: viewport === 'mobile' ? '1fr 1fr' : 'repeat(4, 1fr)' }}>
              {benefitItems.map((it, i) => (
                <div key={i} style={{ background: '#fff', padding: 24, borderRadius: 12, textAlign: 'center', border: '1px solid #ECE8F5' }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 14, background: 'linear-gradient(135deg, #F2EEFF, #E5DDFF)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
                  }}>
                    <PiCheckCircle size={28} style={{ color: accent }} weight="fill" />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}>{it}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* COMPARISON */}
      {comparison?.enabled !== false && (
        <section data-lp-section="comparison" style={{ padding: viewport === 'mobile' ? '48px 16px' : '72px 32px', maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 32px', textAlign: 'center', letterSpacing: -0.3 }}>
            {comparison?.headline || 'How we compare'}
          </h2>
          {comparisonRows.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #ECE8F5' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', background: '#FAFAFC', padding: 16, fontSize: 13, fontWeight: 700 }}>
                <span>&nbsp;</span>
                <span style={{ textAlign: 'center', color: accent }}>{comparison?.comparisonLabel || 'Us'}</span>
                <span style={{ textAlign: 'center', color: '#7d7a86' }}>{comparison?.competitorLabel || 'Other brands'}</span>
              </div>
              {comparisonRows.map((row, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', padding: '14px 16px', fontSize: 14, borderTop: '1px solid #F0EDF7' }}>
                  <span style={{ fontWeight: 500 }}>{row.feature}</span>
                  <span style={{ textAlign: 'center' }}>{row.us ? <PiCheckCircle size={20} style={{ color: '#22B364' }} weight="fill" /> : <span style={{ color: '#9892a3' }}>—</span>}</span>
                  <span style={{ textAlign: 'center' }}>{row.them ? <PiCheckCircle size={20} style={{ color: '#22B364' }} weight="fill" /> : <span style={{ color: '#9892a3' }}>—</span>}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* REVIEWS — hidden entirely until the merchant supplies real reviews */}
      {hasReviews && (
      <section data-lp-section="reviews" style={{ padding: viewport === 'mobile' ? '48px 24px' : '72px 32px', maxWidth: 1100, margin: '0 auto', background: '#FAFAFC', borderRadius: 0 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {hasRating && <Stars n={starsAvgNum} size={24} />}
          <h2 style={{ fontSize: 28, fontWeight: 800, margin: '12px 0 4px', letterSpacing: -0.3 }}>{reviews.headline || 'What customers say'}</h2>
          {ratingSummaryText && (
            <div data-lp-field="ratingSummaryText" style={{ fontSize: 14, color: '#4a4458' }}>{ratingSummaryText}</div>
          )}
        </div>
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: viewport === 'mobile' ? '1fr' : 'repeat(3, 1fr)' }}>
          {reviewItems.map((r, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 12, padding: 22, border: '1px solid #ECE8F5' }}>
              {(r.stars || r.rating) ? <Stars n={r.stars || r.rating} /> : null}
              <p style={{ fontSize: 14, lineHeight: 1.6, color: '#1a1a1a', margin: '10px 0 16px', fontWeight: 500 }}>"{r.quote || r.text}"</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: avatarBg(r.name || r.initials || `R${i}`), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#1F1B33' }}>
                  {initialsFor(r, i)}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                  {r.verified && verifiedLabel && (
                    <div data-lp-field="verifiedLabel" style={{ fontSize: 11, color: '#22B364', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <PiCheckCircle size={12} weight="fill" /> {verifiedLabel}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* GUARANTEE BADGE */}
      {guarantee.enabled !== false && (
        <section data-lp-section="guarantee" style={{ padding: viewport === 'mobile' ? '48px 24px' : '64px 32px', textAlign: 'center' }}>
          <div style={{
            maxWidth: 720, margin: '0 auto', padding: viewport === 'mobile' ? 28 : 40,
            background: 'linear-gradient(135deg, #F8F4FF 0%, #fff 100%)',
            borderRadius: 16, border: `2px solid ${accent}33`,
          }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%', background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
              boxShadow: '0 4px 16px rgba(134,97,255,0.2)',
            }}>
              <PiShieldCheck size={36} style={{ color: accent }} weight="fill" />
            </div>
            <h3 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 8px', letterSpacing: -0.3 }}>
              {guarantee.headline || '30-Day Money-Back Guarantee'}
            </h3>
            <p style={{ fontSize: 15, color: '#4a4458', margin: 0, lineHeight: 1.6 }}>
              {guarantee.body || "Try it risk-free. If you don't love it, send it back within 30 days for a full refund — no questions asked."}
            </p>
          </div>
        </section>
      )}

      {/* FAQ */}
      {(() => {
        const faqItems = (faq.items || []).filter((it) => (it.question || '').trim() && (it.answer || '').trim());
        if (faqItems.length === 0) return null;
        return (
          <section data-lp-section="faq" style={{ padding: viewport === 'mobile' ? '48px 24px' : '64px 32px', background: '#FAFAFC' }}>
            <div style={{ maxWidth: 720, margin: '0 auto' }}>
              <h2 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 28px', textAlign: 'center', letterSpacing: -0.3 }}>
                {faq.headline || 'Frequently asked questions'}
              </h2>
              {faqItems.map((it, i) => (
                <details key={i} style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', marginBottom: 10, border: '1px solid #ECE8F5' }} open={i === 0}>
                  <summary style={{ fontSize: 15, fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#1F1B33' }}>
                    {it.question}
                    <PiCaretDown size={16} style={{ color: '#7d7a86' }} />
                  </summary>
                  <p style={{ fontSize: 14, color: '#4a4458', margin: '12px 0 0', lineHeight: 1.6 }}>
                    {it.answer}
                  </p>
                </details>
              ))}
            </div>
          </section>
        );
      })()}

      {/* FINAL CTA */}
      <section data-lp-section="finalCta" style={{ padding: viewport === 'mobile' ? '64px 24px' : '88px 32px', textAlign: 'center', background: 'linear-gradient(135deg, #1F1B33 0%, #2D2849 100%)', color: '#fff' }}>
        {finalCtaEyebrow && (
          <div style={{ fontSize: 12, color: accent, fontWeight: 800, letterSpacing: 2, marginBottom: 12 }}>{finalCtaEyebrow}</div>
        )}
        <h2 style={{ fontSize: viewport === 'mobile' ? 32 : 40, fontWeight: 900, margin: '0 0 12px', letterSpacing: -0.8 }}>
          {finalCta.headline || 'Ready to get yours?'}
        </h2>
        <p style={{ fontSize: 16, opacity: 0.85, margin: '0 0 28px', maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
          {finalCta.subtext || 'Risk-free 30-day trial. Free shipping. Ships in 24 hours.'}
        </p>
        {finalCta.urgencyText && (
          <p style={{ fontSize: 14, color: accent, fontWeight: 700, margin: '0 0 20px' }}>{finalCta.urgencyText}</p>
        )}
        {renderPrimaryCta(finalCta.ctaLabel || 'Add to cart', { big: true, color: accent, textColor: '#1F1B33' })}
        <div style={{ marginTop: 20, fontSize: 12, opacity: 0.6 }}>
          {finalCta.footnote || '🔒 Secure checkout · 30-day returns · 2-year warranty'}
        </div>
      </section>

      {/* STICKY CTA */}
      {stickyCta.enabled && (
        <div data-lp-section="stickyCta" style={{
          position: 'sticky', bottom: 0, background: '#fff',
          borderTop: '1px solid #E5E0F2', padding: '12px 16px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
          boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{merged.title}</div>
            <div style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'baseline' }}>
              {merged.priceStr && (
                <span style={{ color: '#1F1B33', fontWeight: 700 }}>{merged.priceStr}</span>
              )}
              {merged.compareStr && (
                <span style={{ color: '#9892a3', textDecoration: 'line-through' }}>{merged.compareStr}</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={adding || !merged.variantId}
            style={{
              background: accent, color: '#fff', border: 'none',
              padding: '11px 22px', borderRadius: 'var(--lp-radius, 8px)',
              fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
              cursor: adding || !merged.variantId ? 'not-allowed' : 'pointer',
              opacity: merged.variantId ? 1 : 0.6,
            }}
          >
            {stickyCta.ctaLabel || 'Add to cart →'}
          </button>
        </div>
      )}
    </PreviewFrame>
  );
}
